<ng-template #datePicker>
    <date-picker
        [type]="type"
        [dualCalendar]="isSmallDevice ? false : sideBySide"
        [allowFuture]="allowFuture"
        [lowerLimit]="lowerLimit"
        [upperLimit]="upperLimit"
        [leftArrowSvgSrc]="iconPaths.LeftArrow"
        [rightArrowSvgSrc]="iconPaths.RightArrow"
        [ngModel]="newValue"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="setNewValue($event)"
        (dayClick)="handleDayClick()"
    >
    </date-picker>

    <div
        [ngClass]="{
            'lsq-date-select-mobile-drawer-footer': isSmallDevice$ | async,
            'lsq-dropdown-footer d-flex justify-content-between': !(isSmallDevice$ | async)
        }"
    >
        <ng-container *ngIf="isSmallDevice$ | async">
            <div *ngIf="sideBySide" class="lsq-date-select-mobile-range-options select-group">
                <select
                    id="lsq-date-select-mobile-range-options-select"
                    class="transparent"
                    aria-label="Select a date range"
                    [ngModel]="selectedRange"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="selectRangeOption($event)"
                >
                    <option *ngFor="let range of rangeOptions" [ngValue]="range.value">{{
                        rangeOptionsDisplay[range.value]
                    }}</option>
                </select>
                <lsq-icon
                    class="transparent-select-chevron"
                    [type]="icons.ChevronDown"
                    [color]="color.GrayDarkest"
                >
                </lsq-icon>
            </div>
        </ng-container>

        <div
            [ngClass]="{
                'lsq-date-select-mobile-drawer-value': isSmallDevice$ | async
            }"
        >
            <span class="lsq-date-select-value">
                <ng-container
                    *ngIf="
                        isRangePicker() &&
                        (!displayRangePresetNameAsValue || selectedRange === 'custom')
                    "
                >
                    <form [formGroup]="dateRangeFormGroup" class="date-input-form form-group">
                        <lsq-date-input
                            [name]="'startDateInput'"
                            [ngModel]="startDateValue"
                            [group]="dateRangeFormGroup"
                            [formControlName]="'startDateInput'"
                            (blurred)="handleInputBlur()"
                            (valueChanged)="handleStartDateInputChange($event)"
                        ></lsq-date-input>
                        <span class="range-separator">
                            &nbsp;-&nbsp;
                        </span>
                        <lsq-date-input
                            [name]="'endDateInput'"
                            [ngModel]="endDateValue"
                            [group]="dateRangeFormGroup"
                            [formControlName]="'endDateInput'"
                            (blurred)="handleInputBlur()"
                            (valueChanged)="handleEndDateInputChange($event)"
                        ></lsq-date-input>
                    </form>
                </ng-container>
                <ng-container *ngIf="!isRangePicker()">
                    <form [formGroup]="singleDateFormGroup" class="date-input-form form-group">
                        <lsq-date-input
                            [name]="'singleDateInput'"
                            [ngModel]="singleDateValue"
                            [group]="singleDateFormGroup"
                            [formControlName]="'singleDateInput'"
                            (blurred)="handleInputBlur()"
                            (valueChanged)="handleSingleDateInputChange($event)"
                        ></lsq-date-input>
                    </form>
                </ng-container>

                <ng-container *ngIf="displayRangePresetNameAsValue && selectedRange !== 'custom'">
                    {{ rangeOptionsDisplay[selectedRange] }}
                </ng-container>
            </span>
        </div>
        <div
            [ngClass]="{
                'lsq-date-select-mobile-drawer-action': isSmallDevice$ | async
            }"
        >
            <lsq-button
                class="lsq-date-select-apply-btn"
                ariaLabel="Apply date selection"
                appearance="primary"
                size="block"
                [disabled]="disableApplyButton"
                (buttonClick)="apply()"
            >
                Apply
            </lsq-button>
        </div>
    </div>
</ng-template>

<div
    *ngIf="!(isSmallDevice$ | async)"
    class="form-group select-group lsq-date-select-group"
    [ngClass]="[parentClass, basicLabel ? 'has-basic-label' : 'has-inner-left-label']"
>
    <div
        class="lsq-select-input lsq-date-select-input"
        [ngClass]="[typeClass, expandedClass]"
        [id]="id"
        [attr.tabindex]="'0'"
        clickOutsideEvents="mouseup"
        (clickOutside)="toggleExpanded(false)"
    >
        <label *ngIf="label && basicLabel" class="basic-label" [attr.for]="id">
            {{ label }}
        </label>

        <div
            class="form-control input-group lsq-select-viewport align-content-center"
            [ngClass]="{
                viewportClass: true,
                inputClass: true,
                'is-invalid': showErrorMessage()
            }"
            aria-live="polite"
            (click)="toggleExpanded()"
        >
            <label
                *ngIf="label && !basicLabel"
                class="lsq-date-select-label"
                [ngClass]="{
                    'text-danger': showErrorMessage()
                }"
                [attr.for]="id"
            >
                <strong>{{ label }}</strong>
            </label>

            <span *ngIf="!!model" class="lsq-select-viewport-text">
                <span class="lsq-date-select-value">
                    <ng-container
                        *ngIf="
                            (!displayRangePresetNameAsValue || selectedRange === 'custom') &&
                            (!!getAsDateRange(model).start || !!getAsDateRange(model).end)
                        "
                    >
                        <ng-container *ngIf="!!getAsDateRange(model).start">
                            {{ safelyDisplayDate(getAsDateRange(model).start) }}
                        </ng-container>
                        <span *ngIf="!getAsDateRange(model).start" class="text-gray-600"
                            >(select a start date)</span
                        >
                        &nbsp;-&nbsp;
                        <ng-container *ngIf="!!getAsDateRange(model).end">
                            {{ safelyDisplayDate(getAsDateRange(model).end) }}
                        </ng-container>
                        <span *ngIf="!getAsDateRange(model).end" class="text-gray-600">
                            (select an end date)
                        </span>
                    </ng-container>

                    <ng-container
                        *ngIf="displayRangePresetNameAsValue && selectedRange !== 'custom'"
                    >
                        {{ rangeOptionsDisplay[selectedRange] }}
                    </ng-container>

                    <ng-container *ngIf="!!getAsDate(model)">
                        {{ safelyDisplayDate(getAsDate(model)) }}
                    </ng-container>
                </span>
            </span>

            <span *ngIf="!model" class="lsq-select-viewport-text">
                <span class="lsq-date-select-placeholder">{{ placeholder }}</span>
            </span>

            <lsq-icon
                *ngIf="arrow"
                class="chevron-icon"
                [ngClass]="{
                    black: !hasDarkBackground(),
                    white: hasDarkBackground(),
                    'basic-label-icon': basicLabel
                }"
                [type]="inputIcon"
                [color]="hasDarkBackground() ? color.White : color.Black"
            >
            </lsq-icon>
        </div>

        <lsq-dropdown
            [show]="isExpanded"
            [hasHeader]="sideBySide && type === datePickerType.Range"
            parentClass="has-footer"
            triangle="right"
            [lightTriangle]="true"
        >
            <header
                *ngIf="sideBySide && type === datePickerType.Range && !hideOptions"
                class="header"
            >
                <div class="header-inner">
                    <button
                        *ngFor="let option of rangeOptions"
                        class="col btn-transparent-box dark"
                        [id]="'lsq-date-select-' + option.id"
                        [ngClass]="{ selected: selectedRange === option.value }"
                        [ngStyle]="option.style"
                        (click)="selectRangeOption(option.value)"
                    >
                        {{ rangeOptionsDisplay[option.value] }}
                    </button>
                </div>
            </header>
            <span *ngIf="calendarLabel" class="lsq-date-select-inner-label">
                {{ calendarLabel }}
            </span>
            <ng-container *ngTemplateOutlet="datePicker"></ng-container>
        </lsq-dropdown>
    </div>
    <h6 class="input-error-message text-uppercase font-weight-bold" *ngIf="showErrorMessage()">
        {{ getErrorMessage() }}
    </h6>
</div>

<ng-container *ngIf="isSmallDevice$ | async">
    <div
        class="lsq-date-select-mobile-drawer"
        [ngClass]="{
            'lsq-date-select-mobile-drawer-single': !isRangePicker(),
            'lsq-date-select-mobile-drawer-range': isRangePicker()
        }"
    >
        <ng-container *ngTemplateOutlet="datePicker"></ng-container>
    </div>
</ng-container>
